<script setup lang="ts">
import { computed } from 'vue'
import Icon from '@/components/Base/BaseIcon.vue'
import type { IconName } from '@/components/Base/Icons/icons'

// Note: AppButtonIcon can be replaced by AppButton
const props = withDefaults(
  defineProps<{
    iconName?: IconName
    iconSize?: string
    iconRotationDegree?: number
    classes?: string[]
    iconPosition?: 'left' | 'right'
    isRound?: boolean
    size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl'
    disabled?: boolean
    spacing?: string
  }>(),
  {
    iconPosition: 'right',
    size: 'base',
    classes: () => [],
  }
)

const showLeftIcon = computed(() => {
  return props.iconName && props.iconPosition === 'left'
})

const showRightIcon = computed(() => {
  return props.iconName && props.iconPosition === 'right'
})

const iconSpacing = computed(() => {
  if (props.spacing) {
    return props.spacing
  }

  if (props.iconName) {
    if (props.size === 'lg' || props.size === 'xl') {
      return '1rem'
    }

    return '0.75rem'
  }

  return '0px'
})

const radiusClasses = computed(() => {
  if (props.isRound) {
    return 'rounded-[25px]'
  }

  if (props.size === 'xs') {
    return 'rounded'
  }

  return 'rounded-md'
})

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'xs':
      return 'text-xs py-[7px] px-[15px]'
    case 'sm':
      return 'text-sm py-[9px] px-[17px]'
    case 'base':
      return 'leading-5 py-[9px] px-[19px]'
    case 'lg':
      return 'py-[9px] px-4'
    case 'xl':
      return 'text-md leading-6 py-[13px] px-6'
  }
})
</script>

<template>
  <button
    class="app-button"
    type="button"
    :class="[
      ...classes,
      sizeClasses,
      radiusClasses,
      { 'app-button--disabled': disabled },
    ]"
    :disabled="disabled"
  >
    <Icon
      v-if="showLeftIcon"
      :name="iconName!"
      :size="iconSize"
      :rotation-degree="iconRotationDegree"
      :style="{ 'margin-right': iconSpacing }"
    />

    <!-- button text -->
    <slot />

    <Icon
      v-if="showRightIcon"
      :name="iconName!"
      :size="iconSize"
      :rotation-degree="iconRotationDegree"
      :style="{ 'margin-left': iconSpacing }"
    />
  </button>
</template>
